import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import DrillCard from '../components/cards/DrillCard';
import StatCard from '../components/cards/StatCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import DashboardNavigation, { AvatarMenu } from '../components/DashboardNavigation';
import MobileHeaderDropdown from '../components/MobileHeaderDropdown';
import ImpersonationBanner from '../components/ImpersonationBanner';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import { formatDate, formatStatType, isDrillOverdue } from '../utils/formatters';
import {
  getStatusBadgeStyle,
  getCategoryBadgeStyle,
  getDifficultyBadgeStyle,
} from '../utils/badgeStyles';

const PlayerDashboardPage = () => {
  const { profile } = useAuth();
  const { isDesktop } = useResponsive();
  const [assignedDrills, setAssignedDrills] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [playerNotes, setPlayerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [drillVideoUrl, setDrillVideoUrl] = useState(null);
  const [drillThumbnailUrl, setDrillThumbnailUrl] = useState(null);

  useEffect(() => {
    if (profile?.id) {
      loadAssignedDrills();
      loadPlayerStats();
      loadPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Load video and thumbnail URLs when drill is selected
  useEffect(() => {
    const loadDrillMedia = async () => {
      if (!selectedDrill?.drills) {
        setDrillVideoUrl(null);
        setDrillThumbnailUrl(null);
        return;
      }

      const drill = selectedDrill.drills;

      // Load video URL
      if (drill.video_storage_path) {
        try {
          const { data, error } = await supabase.storage
            .from('drill-videos')
            .createSignedUrl(drill.video_storage_path, 31536000); // 1 year

          if (!error && data?.signedUrl) {
            setDrillVideoUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading video:', err);
        }
      } else if (drill.video_url) {
        setDrillVideoUrl(drill.video_url);
      } else {
        setDrillVideoUrl(null);
      }

      // Load thumbnail URL
      if (drill.thumbnail_storage_path) {
        try {
          const { data, error } = await supabase.storage
            .from('drill-thumbnails')
            .createSignedUrl(drill.thumbnail_storage_path, 31536000); // 1 year

          if (!error && data?.signedUrl) {
            setDrillThumbnailUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading thumbnail:', err);
        }
      } else if (drill.thumbnail_url) {
        setDrillThumbnailUrl(drill.thumbnail_url);
      } else {
        setDrillThumbnailUrl(null);
      }
    };

    loadDrillMedia();
  }, [selectedDrill]);

  const loadAssignedDrills = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('assigned_drills')
        .select(
          `
          *,
          drills (
            id,
            name,
            description,
            category,
            difficulty_level,
            video_url,
            video_storage_path,
            video_source,
            thumbnail_url,
            thumbnail_storage_path,
            thumbnail_source,
            duration_minutes
          )
        `
        )
        .eq('player_id', profile.id)
        .order('due_date', { ascending: true, nullsLast: true })
        .order('assigned_date', { ascending: false });

      if (error) throw error;
      setAssignedDrills(data || []);
    } catch (error) {
      console.error('Error loading assigned drills:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerStats = async () => {
    try {
      console.log('Loading stats for player:', profile.id);
      const { data, error } = await supabase
        .from('player_stats')
        .select(
          `
          *,
          category:stat_categories(
            id,
            name,
            unit,
            sort_direction
          )
        `
        )
        .eq('player_id', profile.id)
        .order('recorded_date', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Supabase error loading stats:', error);
        throw error;
      }

      console.log('Stats loaded:', data?.length || 0, 'records');

      // Load rank information for each stat
      const statsWithRanks = await Promise.all(
        (data || []).map(async (stat) => {
          // Only calculate rank if stat has a category and player has a team
          if (!stat.stat_category_id || !profile.team_id) {
            return { ...stat, rank: null };
          }

          try {
            // Get rank for this specific stat entry (all stats mode)
            const { data: rankData, error: rankError } = await supabase.rpc(
              'get_player_stat_rank_all',
              {
                p_stat_id: stat.id,
                p_stat_category_id: stat.stat_category_id,
                p_team_id: profile.team_id,
              }
            );

            if (rankError) {
              console.error('Error loading rank:', rankError);
              return { ...stat, rank: null };
            }

            return {
              ...stat,
              rank: rankData && rankData.length > 0 ? rankData[0] : null,
            };
          } catch (err) {
            console.error('Error calculating rank:', err);
            return { ...stat, rank: null };
          }
        })
      );

      setPlayerStats(statsWithRanks);
    } catch (error) {
      console.error('Error loading player stats:', error);
      setError(error.message);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('family_link_requests')
        .select(
          `
          *,
          parent:profiles!family_link_requests_parent_id_fkey(full_name)
        `
        )
        .eq('player_id', profile.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingRequests(data || []);
    } catch (error) {
      console.error('Error loading family link requests:', error);
    }
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const { data, error } = await supabase.rpc('approve_family_link_request', {
        request_id: selectedRequest.id,
      });

      if (error) throw error;

      const result = data[0];
      if (!result.success) {
        throw new Error(result.error_message);
      }

      setSuccess(`You are now linked to ${selectedRequest.parent.full_name}'s family!`);
      setShowApproveModal(false);
      setSelectedRequest(null);
      await loadPendingRequests();

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error approving family link request:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async (request) => {
    if (
      !window.confirm(
        `Are you sure you want to decline the request from ${request.parent.full_name}?`
      )
    ) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data, error } = await supabase.rpc('decline_family_link_request', {
        request_id: request.id,
      });

      if (error) throw error;

      const result = data[0];
      if (!result.success) {
        throw new Error(result.error_message);
      }

      setSuccess('Request declined.');
      await loadPendingRequests();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error declining family link request:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (drill) => {
    setSelectedDrill(drill);
    setShowDetailsModal(true);
  };

  const handleMarkComplete = (drill) => {
    setSelectedDrill(drill);
    setPlayerNotes('');
    setShowCompletionModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowCompletionModal(false);
    setShowDetailsModal(false);
    setSelectedDrill(null);
    setPlayerNotes('');
    setError('');
    setSuccess('');
  };

  const handleSubmitCompletion = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const updates = {
        completed: true,
        completed_date: new Date().toISOString().split('T')[0],
        player_notes: playerNotes || null,
      };

      const { error } = await supabase
        .from('assigned_drills')
        .update(updates)
        .eq('id', selectedDrill.id)
        .eq('player_id', profile.id);

      if (error) throw error;

      setSuccess('Drill marked as complete!');
      handleCloseModal();
      await loadAssignedDrills();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error marking drill complete:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    },
    header: {
      backgroundColor: 'white',
      borderBottom: '1px solid #e0e0e0',
      padding: isDesktop ? '20px 40px' : '16px',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      gap: isDesktop ? '0' : '12px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerLogo: {
      height: isDesktop ? '56px' : '45px',
      width: isDesktop ? 'auto' : '45px',
      objectFit: 'contain',
    },
    mobileHeaderContainer: {
      display: 'grid',
      gridTemplateColumns: '45px 1fr 45px',
      alignItems: 'center',
      width: '100%',
      gap: '12px',
    },
    mobileHeaderTitle: {
      textAlign: 'center',
      overflow: 'visible',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    headerButtons: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    successButton: {
      backgroundColor: '#28a745',
      color: 'white',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
      color: 'white',
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
      color: 'white',
    },
    content: {
      padding: isDesktop ? '40px' : '16px',
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxWidth: '100%',
      width: '100%',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '600px',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: '#666',
      fontSize: '14px',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
      marginBottom: '20px',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #cfc',
      marginBottom: '20px',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    smallButton: {
      padding: isDesktop ? '6px 12px' : '10px 16px',
      fontSize: isDesktop ? '12px' : '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#999',
      fontSize: '14px',
    },
    overdueText: {
      color: '#dc3545',
      fontWeight: '600',
    },
    // Modal styles
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: isDesktop ? '8px' : '8px 8px 0 0',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '20px',
      maxWidth: isDesktop ? '600px' : '100%',
      width: isDesktop ? '90%' : '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxSizing: 'border-box',
      ...(!isDesktop && {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        margin: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }),
    },
    modalHeader: {
      marginBottom: '20px',
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#333',
      margin: '0 0 10px 0',
    },
    modalSubtitle: {
      fontSize: '14px',
      color: '#666',
      margin: 0,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    textarea: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    actions: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '10px',
    },
    drillDetails: {
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '10px',
    },
    drillDetailItem: {
      marginBottom: '8px',
      fontSize: '14px',
    },
    // Mobile card styles
    drillCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: '16px',
      marginBottom: '12px',
      border: '1px solid #e0e0e0',
    },
    drillCardsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
    },
    mobileCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
      paddingBottom: '12px',
      borderBottom: '1px solid #f0f0f0',
    },
    mobileCardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      flex: 1,
      marginRight: '8px',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },
    mobileCardBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '12px',
      fontSize: '14px',
    },
    mobileCardRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '4px',
    },
    mobileCardLabel: {
      fontWeight: '600',
      color: '#666',
      fontSize: '13px',
    },
    mobileCardValue: {
      color: '#333',
      fontSize: '13px',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'stretch',
    },
    cardActionButton: {
      flex: 1,
      padding: '12px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      textAlign: 'center',
      minHeight: '44px',
    },
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.container}>
      <ImpersonationBanner />
      <header style={styles.header}>
        {isDesktop ? (
          <>
            <div style={styles.headerLeft}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <h1 style={styles.title}>Player Dashboard</h1>
            </div>
            <DashboardNavigation />
          </>
        ) : (
          <div style={styles.mobileHeaderContainer}>
            <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
            <div style={styles.mobileHeaderTitle}>
              <MobileHeaderDropdown />
            </div>
            <AvatarMenu />
          </div>
        )}
      </header>

      <div style={styles.content}>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* Pending Family Link Requests */}
        {pendingRequests.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Family Link Requests ({pendingRequests.length})</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                        {request.parent.full_name}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        Wants to link you to their family
                      </div>
                    </div>
                    <span
                      style={{
                        ...styles.badge,
                        backgroundColor: '#fff3e0',
                        color: '#f57c00',
                      }}
                    >
                      Pending
                    </span>
                  </div>
                  {request.parent_message && (
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        fontStyle: 'italic',
                      }}
                    >
                      "{request.parent_message}"
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>
                    Sent {formatDate(request.created_at)} • Expires {formatDate(request.expires_at)}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexDirection: isDesktop ? 'row' : 'column',
                    }}
                  >
                    <button
                      style={{
                        ...styles.smallButton,
                        backgroundColor: '#28a745',
                        color: 'white',
                        flex: 1,
                      }}
                      onClick={() => handleApproveClick(request)}
                      disabled={submitting}
                    >
                      Approve
                    </button>
                    <button
                      style={{
                        ...styles.smallButton,
                        backgroundColor: '#dc3545',
                        color: 'white',
                        flex: 1,
                      }}
                      onClick={() => handleDecline(request)}
                      disabled={submitting}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assigned Drills Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>My Assigned Drills</h2>
          </div>

          {assignedDrills.length === 0 ? (
            <div style={styles.emptyState}>No drills assigned yet. Check back later!</div>
          ) : isDesktop ? (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Drill Name</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Difficulty</th>
                    <th style={styles.th}>Assigned</th>
                    <th style={styles.th}>Due Date</th>
                    <th style={styles.th}>Coach Notes</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedDrills.map((assignment) => (
                    <tr key={assignment.id}>
                      <td style={styles.td}>{assignment.drills?.name || 'N/A'}</td>
                      <td style={styles.td}>
                        <span style={getCategoryBadgeStyle(assignment.drills?.category)}>
                          {assignment.drills?.category || 'N/A'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={getDifficultyBadgeStyle(assignment.drills?.difficulty_level)}>
                          {assignment.drills?.difficulty_level || 'N/A'}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDate(assignment.assigned_date)}</td>
                      <td
                        style={{
                          ...styles.td,
                          ...(isDrillOverdue(assignment.due_date, assignment.completed) &&
                            styles.overdueText),
                        }}
                      >
                        {formatDate(assignment.due_date)}
                        {isDrillOverdue(assignment.due_date, assignment.completed) && ' (Overdue)'}
                      </td>
                      <td style={styles.td}>
                        {assignment.coach_notes ? (
                          assignment.coach_notes.length > 50 ? (
                            `${assignment.coach_notes.substring(0, 50)}...`
                          ) : (
                            assignment.coach_notes
                          )
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No notes</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={getStatusBadgeStyle(assignment.completed)}>
                          {assignment.completed ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            style={{
                              ...styles.smallButton,
                              backgroundColor: '#007bff',
                              color: 'white',
                            }}
                            onClick={() => handleViewDetails(assignment)}
                          >
                            View Details
                          </button>
                          {!assignment.completed && (
                            <button
                              style={{
                                ...styles.smallButton,
                                backgroundColor: '#28a745',
                                color: 'white',
                              }}
                              onClick={() => handleMarkComplete(assignment)}
                            >
                              Mark Complete
                            </button>
                          )}
                          {assignment.completed && (
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              Completed {formatDate(assignment.completed_date)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.drillCardsContainer}>
              {assignedDrills.map((assignment) => (
                <DrillCard
                  key={assignment.id}
                  assignment={assignment}
                  onViewDetails={handleViewDetails}
                  onMarkComplete={handleMarkComplete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Stats History Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>My Stats History</h2>
          </div>

          {playerStats.length === 0 ? (
            <div style={styles.emptyState}>No stats recorded yet.</div>
          ) : isDesktop ? (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Stat Type</th>
                    <th style={styles.th}>Value</th>
                    <th style={styles.th}>Team Rank</th>
                    <th style={styles.th}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {playerStats.map((stat) => (
                    <tr key={stat.id}>
                      <td style={styles.td}>{formatDate(stat.recorded_date)}</td>
                      <td style={styles.td}>
                        {stat.category?.name || formatStatType(stat.stat_type)}
                      </td>
                      <td style={styles.td}>
                        {stat.value} {stat.unit}
                      </td>
                      <td style={styles.td}>
                        {stat.rank ? (
                          <span style={{ fontWeight: '600' }}>
                            #{stat.rank.stat_rank} of {stat.rank.total_stats}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {stat.notes
                          ? stat.notes.length > 50
                            ? `${stat.notes.substring(0, 50)}...`
                            : stat.notes
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.drillCardsContainer}>
              {playerStats.map((stat) => (
                <StatCard key={stat.id} stat={stat} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drill Details Modal */}
      {showDetailsModal && selectedDrill && (
        <div style={styles.overlay} onClick={handleCloseModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Drill Details</h2>
              <p style={styles.modalSubtitle}>
                <strong>{selectedDrill.drills?.name}</strong>
              </p>
            </div>

            {/* Video Player */}
            {drillVideoUrl && (
              <div style={{ marginBottom: '20px' }}>
                <VideoPlayer
                  videoUrl={drillVideoUrl}
                  thumbnailUrl={drillThumbnailUrl}
                  title={selectedDrill.drills?.name}
                />
              </div>
            )}

            <div style={styles.drillDetails}>
              <div style={styles.drillDetailItem}>
                <strong>Description:</strong>{' '}
                {selectedDrill.drills?.description || 'No description'}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Category:</strong> {selectedDrill.drills?.category || 'N/A'}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Difficulty:</strong> {selectedDrill.drills?.difficulty_level || 'N/A'}
              </div>
              {selectedDrill.drills?.duration_minutes && (
                <div style={styles.drillDetailItem}>
                  <strong>Duration:</strong> {selectedDrill.drills.duration_minutes} minutes
                </div>
              )}
              <div style={styles.drillDetailItem}>
                <strong>Assigned:</strong> {formatDate(selectedDrill.assigned_date)}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Due:</strong> {formatDate(selectedDrill.due_date)}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Status:</strong>{' '}
                <span style={getStatusBadgeStyle(selectedDrill.completed)}>
                  {selectedDrill.completed ? 'Completed' : 'Pending'}
                </span>
              </div>
              {selectedDrill.coach_notes && (
                <div style={styles.drillDetailItem}>
                  <strong>Coach Notes:</strong>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedDrill.coach_notes}
                  </div>
                </div>
              )}
              {selectedDrill.completed && selectedDrill.player_notes && (
                <div style={styles.drillDetailItem}>
                  <strong>Your Notes:</strong>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '10px',
                      backgroundColor: '#e8f5e9',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedDrill.player_notes}
                  </div>
                </div>
              )}
              {selectedDrill.completed && (
                <div style={styles.drillDetailItem}>
                  <strong>Completed On:</strong> {formatDate(selectedDrill.completed_date)}
                </div>
              )}
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={handleCloseModal}
              >
                Close
              </button>
              {!selectedDrill.completed && (
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.successButton }}
                  onClick={() => {
                    handleCloseModal();
                    setTimeout(() => handleMarkComplete(selectedDrill), 100);
                  }}
                >
                  Mark as Complete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drill Completion Modal */}
      {showCompletionModal && selectedDrill && (
        <div style={styles.overlay} onClick={handleCloseModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Mark Drill Complete</h2>
              <p style={styles.modalSubtitle}>
                Complete drill: <strong>{selectedDrill.drills?.name}</strong>
              </p>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.drillDetails}>
              <div style={styles.drillDetailItem}>
                <strong>Description:</strong>{' '}
                {selectedDrill.drills?.description || 'No description'}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Category:</strong> {selectedDrill.drills?.category || 'N/A'}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Difficulty:</strong> {selectedDrill.drills?.difficulty_level || 'N/A'}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Assigned:</strong> {formatDate(selectedDrill.assigned_date)}
              </div>
              <div style={styles.drillDetailItem}>
                <strong>Due:</strong> {formatDate(selectedDrill.due_date)}
              </div>
              {selectedDrill.coach_notes && (
                <div style={styles.drillDetailItem}>
                  <strong>Coach Notes:</strong> {selectedDrill.coach_notes}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmitCompletion} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Your Notes (Optional)</label>
                <textarea
                  style={styles.textarea}
                  value={playerNotes}
                  onChange={(e) => setPlayerNotes(e.target.value)}
                  placeholder="Add any notes about completing this drill..."
                />
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ ...styles.button, ...styles.successButton }}
                  disabled={submitting}
                >
                  {submitting ? 'Marking Complete...' : 'Mark as Complete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {showApproveModal && selectedRequest && (
        <div style={styles.overlay} onClick={() => setShowApproveModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Link with Family?</h2>
              <p style={styles.modalSubtitle}>
                Do you want to join <strong>{selectedRequest.parent.full_name}'s</strong> family?
              </p>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.drillDetails}>
              <div style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
                <strong>What this means:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>They will be able to view your drills and stats</li>
                  <li>They can mark drills as complete on your behalf</li>
                  <li>You'll be part of their family group</li>
                </ul>
              </div>
              <div
                style={{
                  backgroundColor: '#fff3e0',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#f57c00',
                  fontWeight: '500',
                }}
              >
                ⚠️ You can only be in one family at a time.
              </div>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowApproveModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...styles.successButton,
                  opacity: submitting ? 0.6 : 1,
                }}
                onClick={handleApprove}
                disabled={submitting}
              >
                {submitting ? 'Linking...' : 'Yes, Link Me'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PlayerDashboardPage;
